import { useEffect, useSyncExternalStore, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/app/providers/auth-context'
import { AppShell } from '@/components/layout/AppShell'
import { PublicShell } from '@/components/layout/PublicShell'
import { StorefrontShell } from '@/components/layout/StorefrontShell'
import { resolveChrome } from '@/app/router/resolveChrome'
import { useStorefrontClub } from '@/app/router/useStorefrontClub'
import {
  storefrontReturnTarget,
  storefrontReturnVersion,
  subscribeStorefrontReturn,
} from '@/features/clubs/storefrontReturn'
import { confirmRememberedClub } from '@/features/clubs/storefrontReturnCheck'
import { ScrollToTop } from '@/app/router/ScrollToTop'
import { chooseHome, isPersonalHome } from '@/features/home/personal/homeChoice'
import { hasSessionHint } from '@/lib/auth/tokenStore'

/**
 * Resolves the AppShell surface from the pathname — the ONE place a
 * route opts into the muted admin layer (workspace-jarvis D6,
 * tournament-wizard-jarvis §3): muted tokens, no scanlines (AppShell
 * already handles both). Extend the list, never duplicate the
 * mechanism.
 */
const ADMIN_SURFACE_PREFIXES = ['/workspace', '/organization', '/tournaments/new', '/admin']

function isAdminSurfacePath(pathname: string): boolean {
  if (ADMIN_SURFACE_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return true
  // The tournament editor (`/tournaments/:id/edit`) is a manager surface, but
  // the public tournament page (`/tournaments/:shortId`) is not — match only
  // the `/edit` leaf (the more-specific editor route wins in the router too).
  return pathname.startsWith('/tournaments/') && pathname.endsWith('/edit')
}

export function RoutedShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  // A club wears its OWN chrome on its own page. Null for every other route, for a club
  // that published no public slug, and for one whose read has not arrived yet — which is
  // why the player shell stays the fallback and why this identity, not the pathname
  // alone, is what licenses the storefront below.
  const storefrontClub = useStorefrontClub(pathname)
  // `/` and `/help` wear the app chrome for a signed-in visitor — the same answer
  // `HomeEntry` renders the home from (session hint on first paint, then auth).
  const { state } = useAuth()
  const signedInVisitor = isPersonalHome(chooseHome(state, hasSessionHint()))
  // The club this tab walked out of survives a reload (owner, 2026-10-16), drawn from the
  // tab's note until one read confirms it; a club the server no longer has is forgotten,
  // and this subscription redraws the header when that happens.
  useSyncExternalStore(subscribeStorefrontReturn, storefrontReturnVersion)
  useEffect(() => {
    void confirmRememberedClub(pathname)
  }, [pathname])
  // A club this tab walked out of keeps its bar on our legal pages (resolveChrome).
  const clubRemembered = storefrontReturnTarget(pathname) !== null
  const chrome = resolveChrome(pathname, Boolean(storefrontClub), signedInVisitor, clubRemembered)
  // Front-of-house surfaces use the two-mode public header; auth pages get its
  // logo-only variant. Only the signed-in app keeps the sidebar/drawer AppShell
  // (with its muted admin tone on the manage/create surfaces).
  let shell: ReactNode
  if (chrome === 'storefront' && storefrontClub) {
    shell = <StorefrontShell club={storefrontClub}>{children}</StorefrontShell>
  } else if (chrome === 'marketing' || chrome === 'player') {
    shell = <PublicShell mode={chrome}>{children}</PublicShell>
  } else if (chrome === 'auth-minimal') {
    shell = <PublicShell mode="auth">{children}</PublicShell>
  } else {
    shell = <AppShell surface={isAdminSurfacePath(pathname) ? 'admin' : 'default'}>{children}</AppShell>
  }
  return (
    <>
      <ScrollToTop />
      {shell}
    </>
  )
}
